import { Help_1Icon } from "vega-icons";

import { tenantConfig } from "@tenant-config";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { EmailContactModal } from "@vega-core/components/Modals/EmailContactModal/EmailContactModal";
import { useTheme } from "@vega-core/context/ThemeContext/ThemeContext";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { type FAQItemType } from "@vega-core/models/faq.type";
import { m } from "@vega-core/paraglide/messages";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { HelpModal } from "./HelpModal";

type Props = {
  faqItems?: FAQItemType[];
  title?: string;
};

export const AuthHeader = ({ faqItems, title }: Props) => {
  const {
    global: { companyName },
    contacts,
  } = usePublicConfig();
  const { theme } = useTheme();
  const {
    branding: {
      logos: { logoLight, logoDark, smallLogoLight, smallLogoDark, auth },
    },
  } = tenantConfig;

  const logo = theme === "light" ? logoLight : logoDark;
  const smallLogo = theme === "light" ? smallLogoLight : smallLogoDark;
  const authLogo = auth?.logo ?? logo;
  const authSmallLogo = auth?.smallLogo ?? smallLogo;
  const isHelpModalOpen = useBoolean(false);
  const isContactModalOpen = useBoolean(false);

  return (
    <>
      {isTruthy(contacts.helpEmail) && (
        <HelpModal
          faqItems={faqItems}
          onClose={isHelpModalOpen.setFalse}
          onOpenContactUs={isContactModalOpen.setTrue}
          open={isHelpModalOpen.value}
        />
      )}
      {isTruthy(contacts.helpEmail) && (
        <EmailContactModal
          email={contacts.helpEmail}
          onClose={isContactModalOpen.setFalse}
          open={isContactModalOpen.value}
        />
      )}
      <header className="h-11 flex items-center justify-between lg:mb-6">
        <div className="flex items-center h-5">
          <div className="shadow-card sm:shadow-none h-10 w-10 sm:h-auto sm:w-auto sm:p-0 flex items-center justify-center rounded-lg">
            <picture className="h-5 w-5 sm:h-auto sm:w-auto">
              <source media="(max-width: 840px)" srcSet={authSmallLogo} />
              <img
                alt={companyName}
                className="sm:w-auto sm:h-5 mx-auto"
                src={authLogo}
              />
            </picture>
          </div>
          <div className="border-r-1 border-border/auth/border w-0.5 h-full ml-4 mr-4" />
          <div
            className="text-text/auth/secondary body-large-regular"
            data-testid="auth-header-title"
          >
            {title ?? m.auth_loginHeaderTitle()}
          </div>
        </div>
        <ThemedButton
          aria-label="help"
          className="gap-2"
          dataTestId="help-button"
          onClick={isHelpModalOpen.setTrue}
          variant="secondary"
        >
          <span className="hidden md:inline">{m.btn_needHelp()}</span>
          <span className="md:hidden">
            <Help_1Icon />
          </span>
        </ThemedButton>
      </header>
    </>
  );
};
